import React from 'react'
import { Helmet } from 'react-helmet-async'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import ConfirmDialog from '../partials/ConfirmDialog'
import AppButton from '../partials/Navigation/AppButton'
import { CapitalButton } from '../partials/Navigation/Buttons'
import NavigationBar from '../partials/NavigationBar'
import StatusSave from '../partials/StatusSave'
import withAppStore from '../../util/withAppStore'

import Footer from '../partials/Footer'
import { TextInput, } from '../partials/Inputs/Inputs'

import styles from '../../../css/home.module.css'
import stylesDevice from '../../../css/devicePage.module.css'
import stylesSettings from '../../../css/settings.module.css'

import AppStore from '../../stores/AppStore'
import AppDispatcher from '../../dispatchers/AppDispatcher'

function CustomerDeviceGroupEditPage (props) {
  const navigate = useNavigate()
  const { deviceType, deviceGroup, groupID } = useParams()

  const [state, setState] = React.useState({
    devices: undefined,
    deviceGroup,
    isValid: false,
    name: undefined,
    statusMessageIsVisible: false,
    outcome: undefined,
    users: undefined,
    confirmDeactivateDialogVisible: false,
  })

  const goTo = React.useCallback((x) => navigate(x), [navigate])

  const childChange = React.useCallback((event) => {
    const {
      target: { name: field, value },
    } = event

    handleChange(field, value)
  }, [])

  const confirmDeactivateDeviceGroup = React.useCallback(() => {
    setState(prevState => ({ ...prevState, confirmDeactivateDialogVisible: true }))
  }, [])

  const deactivateGroup = React.useCallback(() => {
    const group = AppStore.getGroup()
    delete group.devices

    AppDispatcher.dispatch({
      action: 'set-group',
      group: {
        id: group.id,
        is_active: false,
      },
      update: true,
    })

    window.setTimeout(() => {
      hideConfirmDeactivateDialog()
      goBack()
    }, 2000)
  }, [])

  const goBack = React.useCallback(() => {
    const { id: customerID } = AppStore.getCustomer()
    goTo(`/${customerID}/settings/deviceGroups`)
  }, [goTo])

  const handleDeactivateResponse = React.useCallback((error) => {
    setState(prevState => ({
      ...prevState,
      statusMessageIsVisible: true,
      outcome: error ? 'failure' : 'success',
    }))
    goBack()
  }, [goBack])

  const hideConfirmDeactivateDialog = React.useCallback(() => {
    setState(prevState => ({ ...prevState, confirmDeactivateDialogVisible: false }))
  }, [])

  const handleChange = React.useCallback((field, value, callback) => {
    setState(prevState => {
      const newState = {
        ...prevState,
        [field]: value,
      }

      const { icon, name } = newState

      const notEmpty = (value) => typeof value === 'string' && value.length > 0

      newState.isValid = notEmpty(name) && notEmpty(icon)

      return newState
    })

    if (callback) callback()
  }, [])

  const saveGroup = React.useCallback(() => {
    const { name, icon } = state
    const group = AppStore.getGroup()
    group.name = name
    group.icon = icon

    AppDispatcher.dispatch({
      action: 'set-group',
      group,
      update: true,
      callback: () => goBack(),
    })
  }, [state, goBack])

  const getGroup = React.useCallback(() => {
    AppDispatcher.dispatch({
      action: 'get-group',
      groupID,
      params: {
        includeDevices: true,
        includeUsers: true,
      },
      callback: () => {
        const { devices, icon, name, users } = AppStore.getGroup()

        setState(prevState => ({
          ...prevState,
          devices,
          icon,
          name,
          users,
        }))
      },
    })
  }, [groupID])

  React.useEffect(() => {
    AppStore.addChangeListener(handleChange)

    return () => {
      AppStore.removeChangeListener(handleChange)
    }
  }, [handleChange])

  React.useEffect(() => {
    getGroup()
  }, [getGroup])

  const {
    auth: { logout },
    intl: { formatMessage },
    pages: { CustomerDeviceGroupEditPage: title },
    route: { path },
  } = props

  const {
    confirmDeactivateDialogVisible,
    devices,
    deviceGroup: deviceGroupState,
    isValid,
    outcome,
    statusMessageIsVisible,
    name: nameState,
    users,
  } = state

  let usersList = []

  if (Array.isArray(users)) {
    const filteredUsers = users
      .filter((user) => !user.email.endsWith('@altrac.io') && !user.email.endsWith('@semios.com'))

    usersList = filteredUsers
      .map((user) => {
        const {
          name: userName,
          first_name: firstName,
          last_name: lastName,
          email,
        } = user

        const fullName = !!firstName && !!lastName && `${firstName} ${lastName}`

        return userName || fullName || email
      })
      .sort((a, b) => a.localeCompare(b))
      .map((name) => <li key={`${name}+${Math.random()}`}>{name}</li>)
  }

  const icons = {
    // AddNew: 'AddNew',
    Bin: 'Bin',
    ColdAirDrain: 'ColdAirDrain',
    Flow: 'Flow',
    // Level: 'Level',
    Moisture: 'Moisture',
    // Power: 'Power',
    Pump: 'Pump',
    PumpFrostWater: 'PumpFrostWater',
    // Speedometer: 'Speedometer',
    // RedX: 'RedX',
    Thermometer: 'Thermometer',
    // Tractor: 'Tractor',
    Valve: 'Valve',
    Weather: 'Weather',
    WindMachine: 'WindMachine',
  }

  const iconButtons = Object.keys(icons)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => {
      const { icon } = state

      return (
        <AppButton
          application={key}
          checked={icon === key}
          icon={key}
          label=''
          key={key}
          onClick={() => handleChange('icon', icons[key])}
        />
      )
    })

  let saveButton

  if (isValid) {
    saveButton = (
      <CapitalButton
        text={formatMessage({
          id: 'setting.save',
          defaultMessage: 'SAVE',
          description: 'SAVE button',
        })}
        buttonFunction={saveGroup}
        hSize='h2'
        style={styles.saveButton}
      />
    )
  }

  // if there are any devices in the group it can't be deleted
  let trashCan = {
    icon: '',
    func: () => {},
  }

  if (Array.isArray(devices) && devices.length === 0) {
    trashCan = {
      icon: 'fa-trash',
      func: () => confirmDeactivateDeviceGroup(),
    }
  }

  return (
    <div>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        page={path}
        logOut={() => logout(navigate)}
        backFunc={goBack}
        name={title}
        rightControl={trashCan}
      />
      <ConfirmDialog
        cancelCallback={hideConfirmDeactivateDialog}
        confirmCallback={deactivateGroup}
        confirmButtonText={`${formatMessage({ id: 'general.delete', defaultMessage: 'DELETE', description: 'delete - confirm button text' })}`}
        title={`${formatMessage({ id: 'general.warning', defaultMessage: 'WARNING', description: 'popup title' })}`}
        question={`${formatMessage({ id: 'customer.groups.deleteMessage', defaultMessage: 'Deactivate the Group {name}?', description: 'delete user message' }, { name: nameState })}`}
        value={deviceGroupState}
        visible={confirmDeactivateDialogVisible}
      />
      {statusMessageIsVisible && <StatusSave visible outcome={outcome} />}
      <div className={styles.pageWidth}>
        <div className={styles.pageContainer}>
          <div className={stylesDevice.sectionName}>
            <FormattedMessage
              id='user.edit.group.details'
              defaultMessage='Group Details'
              description='Group Details label'
              tagName='h2'
            />
          </div>
          <section
            className={stylesSettings.inputArea}
            style={{ display: 'inline-block' }}
          >
            <FormattedMessage
              id='general.group.name'
              defaultMessage='Name'
              description='Name label'
              tagName='h5'
            />
            <div className={stylesSettings.groupName}>
              <TextInput
                value={nameState}
                field='name'
                childChange={childChange}
                style={stylesSettings.groupNameInput}
              />
            </div>
          </section>
          <section style={{ display: 'inline-block' }}>
            <div className={stylesDevice.sectionName}>
              <FormattedMessage
                id='general.group.icon'
                defaultMessage='Group Icon'
                tagName='h2'
              />
            </div>
          </section>
          <div className={styles.list}>{iconButtons}</div>{' '}
          <div className={stylesDevice.sectionName}>
            <FormattedMessage
              id='user.edit.group.permissions'
              defaultMessage='Group Permissions'
              description='Group Permissions label'
              tagName='h2'
            />
          </div>
          <section
            className={stylesSettings.inputArea}
            style={{ display: 'inline-block' }}
          >
            <ul
              style={{
                listStyle: 'none',
                paddingInlineStart: 0,
                fontSize: '30px',
              }}
            >
              {usersList}
            </ul>
          </section>
          <br />
          <section>
            {saveButton}
          </section>
        </div>
      </div>
      <Footer />
    </div>
  )
}

CustomerDeviceGroupEditPage.propTypes = {
  pages: PropTypes.object,
  data: PropTypes.object,
  auth: PropTypes.object,
  intl: PropTypes.object,
  route: PropTypes.object,
}

export default withAppStore(injectIntl(CustomerDeviceGroupEditPage))
